import { Box } from '@chakra-ui/react';
import {
  LIST_FEATURE_FLAGS,
  trackEvent,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import {
  buildPaymentInstructionRoute,
  buildUnpaidMyOrderRoute,
} from '@diamond/sol-buyer/utils';
import {
  usePwaStore,
  useShipToStore,
  useWaitingPaymentsSummary,
} from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import {
  WAITING_PAYMENT_STICKY_MOBILE_BOTTOM,
  WAITING_PAYMENT_STICKY_MOBILE_SPACER,
} from '../layouts/bottom-nav/bottom-nav-config';
import {
  BUYER_LAYOUT_HORIZONTAL_PADDING,
  PageContainer,
} from '../layouts/layout-constants';
import {
  toWaitingPaymentStickyModel,
  WaitingPaymentSticky,
  WaitingPaymentStickyIntent,
} from './WaitingPaymentSticky';

const getResolvedDestination = (intent: WaitingPaymentStickyIntent) => {
  if (intent.kind === 'instruction' || intent.kind === 'proof_draft') {
    return 'payment_instruction';
  }
  return 'unpaid_orders';
};

const WAITING_PAYMENT_STICKY_ENTER_DURATION = '400ms';
const WAITING_PAYMENT_STICKY_ENTER_EASING = 'ease';

const waitingPaymentStickyEnterSx = {
  '@keyframes waitingPaymentStickyEnterMobile': {
    from: { opacity: 0, transform: 'translateY(100%)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes waitingPaymentStickyEnterDesktop': {
    from: { opacity: 0, transform: 'translateY(-100%)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes waitingPaymentStickyEnterFade': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  animation: {
    base: `waitingPaymentStickyEnterMobile ${WAITING_PAYMENT_STICKY_ENTER_DURATION} ${WAITING_PAYMENT_STICKY_ENTER_EASING} both`,
    lg: `waitingPaymentStickyEnterDesktop ${WAITING_PAYMENT_STICKY_ENTER_DURATION} ${WAITING_PAYMENT_STICKY_ENTER_EASING} both`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: `waitingPaymentStickyEnterFade 200ms ${WAITING_PAYMENT_STICKY_ENTER_EASING} both`,
  },
};

export function WaitingPaymentStickySection() {
  const router = useRouter();
  const isPwaOffline = usePwaStore((state) => state.isPwaOffline);
  const selectedShipToId = useShipToStore((state) => state.selectedShipToId);
  const isPaymentInstructionEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_PAYMENT_INSTRUCTION_PAGE,
    false
  );
  const { data } = useWaitingPaymentsSummary(
    selectedShipToId,
    isPaymentInstructionEnabled && Boolean(selectedShipToId) && !isPwaOffline
  );
  const trackedImpressionRef = useRef<string | null>(null);
  const model = data ? toWaitingPaymentStickyModel(data) : null;
  const impressionKey = model ? `${model.count}:${model.intent.kind}` : null;

  useEffect(() => {
    if (
      !isPaymentInstructionEnabled ||
      isPwaOffline ||
      !selectedShipToId ||
      !model ||
      !impressionKey ||
      trackedImpressionRef.current === impressionKey
    ) {
      return;
    }

    trackedImpressionRef.current = impressionKey;
    trackEvent(TRACKING_EVENTS.VIEW_WAITING_PAYMENT_STICKY, {
      entry_source: 'homepage-sticky',
      ff_payment_instruction_page: true,
      order_count: model.count,
      platform: 'web',
      resolved_destination: getResolvedDestination(model.intent),
      warehouse: selectedShipToId,
    });
  }, [
    impressionKey,
    isPaymentInstructionEnabled,
    isPwaOffline,
    model,
    selectedShipToId,
  ]);

  if (
    !isPaymentInstructionEnabled ||
    isPwaOffline ||
    !selectedShipToId ||
    !model
  ) {
    return null;
  }

  const handleAction = (intent: WaitingPaymentStickyIntent) => {
    const resolvedDestination = getResolvedDestination(intent);
    trackEvent(
      intent.kind === 'unpaid_list'
        ? TRACKING_EVENTS.CLICK_WAITING_PAYMENT_STICKY_LIST
        : TRACKING_EVENTS.CLICK_WAITING_PAYMENT_STICKY,
      {
        entry_source: 'homepage-sticky',
        ff_payment_instruction_page: true,
        platform: 'web',
        resolved_destination: resolvedDestination,
        warehouse: selectedShipToId,
      }
    );

    if (intent.kind === 'instruction') {
      trackEvent(TRACKING_EVENTS.CLICK_UPLOAD_PAYMENT_PROOF, {
        entry_source: 'homepage-sticky',
        ff_payment_instruction_page: isPaymentInstructionEnabled,
        order_meta_id: intent.orderMetaId,
        payment_method: 'bank_transfer',
        platform: 'web',
        resolved_destination: resolvedDestination,
        warehouse: selectedShipToId,
      });
    }

    if (intent.kind !== 'unpaid_list') {
      trackEvent(TRACKING_EVENTS.WFP_ROUTE_RESOLVED, {
        entry_source: 'homepage-sticky',
        ff_payment_instruction_page: isPaymentInstructionEnabled,
        order_meta_id: intent.orderMetaId,
        payment_sub_state: intent.kind,
        platform: 'web',
        presentation: 'page',
        resolved_destination: resolvedDestination,
        source: 'sticky',
        sub_state: intent.kind,
        warehouse: selectedShipToId,
      });
    }

    if (intent.kind === 'instruction' || intent.kind === 'proof_draft') {
      return router.push(
        buildPaymentInstructionRoute({
          orderMetaId: intent.orderMetaId,
          warehouse: selectedShipToId,
        })
      );
    }

    return router.push(buildUnpaidMyOrderRoute());
  };

  return (
    <>
      <Box
        data-testid="waiting-payment-sticky-spacer"
        aria-hidden="true"
        h={{
          base: `calc(${WAITING_PAYMENT_STICKY_MOBILE_SPACER}px + env(safe-area-inset-bottom, 0px))`,
          lg: '0',
        }}
      />
      <Box
        data-testid="waiting-payment-sticky-viewport"
        position="fixed"
        top={{ base: 'auto', lg: '137px' }}
        right={{ base: '0', lg: '24px' }}
        bottom={{
          base: `calc(${WAITING_PAYMENT_STICKY_MOBILE_BOTTOM}px + env(safe-area-inset-bottom, 0px))`,
          lg: 'auto',
        }}
        left={{ base: '0', lg: 'auto' }}
        zIndex="1"
        w={{ base: '100%', lg: '374px' }}
        pointerEvents="none"
      >
        <Box
          as={PageContainer}
          w="100%"
          px={{
            base: 2,
            md: BUYER_LAYOUT_HORIZONTAL_PADDING.md,
            lg: 0,
          }}
          pointerEvents="auto"
          transition="padding .3s"
        >
          <Box
            data-testid="waiting-payment-sticky-enter"
            sx={waitingPaymentStickyEnterSx}
          >
            <WaitingPaymentSticky model={model} onAction={handleAction} />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default WaitingPaymentStickySection;
